export function formatMetricValue(
  value: number | null | undefined,
  format: string | null | undefined,
  decimalPlaces: number | null | undefined,
  fallback: string = '—',
  // K/M abbreviation (task #541): display-only, per-metric setting.
  abbreviate: boolean = false
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback
  const places = Math.max(0, Math.min(2, Math.round(Number(decimalPlaces ?? 0))))

  let formatted: string
  const abs = Math.abs(value)
  if (abbreviate && abs >= 1_000) {
    const [divisor, suffix] = abs >= 1_000_000 ? [1_000_000, 'M'] : [1_000, 'K']
    formatted = `${value < 0 ? '-' : ''}${(abs / divisor).toFixed(places)}${suffix}`
  } else {
    const fixed = value.toFixed(places)
    const [whole, frac] = fixed.split('.')
    const withCommas = Number(whole).toLocaleString('en-US')
    formatted = frac ? `${withCommas}.${frac}` : withCommas
  }

  switch (format) {
    case 'currency':
      return `$${formatted}`
    case 'percentage':
      return `${formatted}%`
    default:
      return formatted
  }
}

const COMPARISON_PREFIXES: Record<string, string> = {
  gte: '>= ',
  lte: '<= ',
  gt: '> ',
  lt: '< ',
  eq: '= ',
}

/**
 * Mirrors Metric#formatted_target on the server: the comparison prefix
 * followed by the metric-formatted target ($/%, commas, K/M). Used to
 * re-render the Target cell optimistically after an inline target edit,
 * before (or instead of) the server's own `formatted_target` lands.
 */
export function formatTargetDisplay(
  target: number | null | undefined,
  comparison: string | null | undefined,
  format: string | null | undefined,
  decimalPlaces: number | null | undefined,
  abbreviate: boolean = false
): string {
  if (target === null || target === undefined) return 'N/A'
  const prefix = COMPARISON_PREFIXES[comparison || 'gte'] ?? '>= '
  return prefix + formatMetricValue(target, format, decimalPlaces, 'N/A', abbreviate)
}
